<div class="loc-lidar-panel" [class.loc-lidar-panel--embedded]="embedded">
  <p class="loc-lidar-panel-loading" *ngIf="detailLoading()">Loading LiDAR scans…</p>

  <ng-container *ngIf="!detailLoading() && detail() as loc">
    <header class="loc-lidar-head" *ngIf="showHeader">
      <h3 class="loc-panel-title" *ngIf="!embedded">{{ loc.name }}</h3>
      <p class="loc-panel-sub">{{ scanScopeNote(loc) }}</p>
    </header>

    <p class="lidar-process-hint" *ngIf="!scanSummary() && hasUploadedScans()">
      Files uploaded. Run <strong>Process selected</strong> to detect buildings and plot counts from the LiDAR scan.
    </p>

    <div class="lidar-analysis" *ngIf="scanSummary() as summary">
      <h4 class="lidar-analysis-title">Results from LiDAR scan</h4>

      <div class="lidar-match-banner" [ngClass]="matchBannerClass(summary)">
        <p class="lidar-match-banner-headline">{{ matchRatioLabel(summary) }}</p>
        <p class="lidar-match-banner-detail">{{ matchBannerDetail(summary) }}</p>
      </div>

      <div class="lidar-analysis-grid">
        <div class="lidar-stat">
          <span class="lidar-stat-value">{{ summary.buildingsDetected ?? '—' }}</span>
          <span class="lidar-stat-label">Buildings detected</span>
        </div>
        <div class="lidar-stat">
          <span class="lidar-stat-value">
            <ng-container *ngIf="registeredPlotCount(summary) > 0; else matchedOnly">
              {{ matchedPlotCount(summary) }}/{{ registeredPlotCount(summary) }}
            </ng-container>
            <ng-template #matchedOnly>{{ matchedPlotCount(summary) || '—' }}</ng-template>
          </span>
          <span class="lidar-stat-label">Plots matched</span>
        </div>
        <div class="lidar-stat">
          <span class="lidar-stat-value">{{ summary.registeredPlotCount ?? '—' }}</span>
          <span class="lidar-stat-label">Plots in scan area</span>
        </div>
      </div>
      <p class="lidar-analysis-note" *ngIf="summary.note">{{ summary.note }}</p>
      <details class="lidar-analysis-details" *ngIf="hasScanIssues(summary)" [open]="!isFullPlotMatch(summary)">
        <summary>Review unmatched items</summary>
        <ul *ngIf="summary.unmatchedBuildings?.length">
          <li *ngFor="let item of summary.unmatchedBuildings">
            Building cluster #{{ (item.detectionIndex ?? 0) + 1 }} — not matched to a registered plot
          </li>
        </ul>
        <ul *ngIf="summary.unmatchedPlots?.length">
          <li *ngFor="let item of summary.unmatchedPlots">
            Khasra {{ item.plotRef }} — no building detected inside plot boundary (manual check recommended)
          </li>
        </ul>
        <ul *ngIf="summary.missingBoundaries?.length">
          <li *ngFor="let plotRef of summary.missingBoundaries">Khasra {{ plotRef }} — plot boundary not registered</li>
        </ul>
      </details>
    </div>

    <div class="loc-scan-row">
      <div class="loc-scan-card">
        <h4>Drone scan</h4>
        <p class="loc-scan-file" *ngIf="scanJobForType('drone') as job; else noDrone">
          {{ fileNameFromUri(job.rawStorageUri) }}
          <span class="loc-status-pill" [ngClass]="scanStatusClass(job.statusCd)">{{ formatScanStatus(job.statusCd) }}</span>
        </p>
        <ng-template #noDrone><p class="loc-scan-file loc-scan-file--empty">No file uploaded</p></ng-template>
        <label class="loc-check" *ngIf="scanJobForType('drone') as dJob">
          <input type="checkbox" [checked]="scanJobSelected(dJob.id)" (change)="toggleScanJob(dJob.id)" /> Include in processing
        </label>
        <div class="loc-scan-card-actions" *ngIf="canManageLocations()">
          <input
            #droneFileInput
            type="file"
            class="loc-scan-file-input"
            accept=".las,.laz,.LAS,.LAZ"
            (change)="onEmbeddedFileSelected($event, 'drone')"
          />
          <button
            type="button"
            class="btn secondary btn-compact"
            [disabled]="saving()"
            (click)="embedded ? triggerEmbeddedUpload('drone', droneFileInput) : openUploadScan('drone')"
          >
            {{ saving() && scanUploadForm.scanType === 'drone' ? 'Uploading…' : 'Upload drone' }}
          </button>
          <button
            type="button"
            class="btn-link loc-scan-remove"
            *ngIf="scanJobForType('drone') as dJob"
            [disabled]="!canRemoveScan(dJob) || deleteImpactLoadingId() === dJob.id || removingScanJobId() === dJob.id"
            (click)="removeScanJob(dJob)"
          >
            {{
              removingScanJobId() === dJob.id
                ? 'Removing…'
                : deleteImpactLoadingId() === dJob.id
                  ? 'Checking…'
                  : 'Remove'
            }}
          </button>
        </div>
      </div>

      <div class="loc-scan-card">
        <h4>Vehicle scan</h4>
        <p class="loc-scan-file" *ngIf="scanJobForType('vehicle') as job; else noVehicle">
          {{ fileNameFromUri(job.rawStorageUri) }}
          <span class="loc-status-pill" [ngClass]="scanStatusClass(job.statusCd)">{{ formatScanStatus(job.statusCd) }}</span>
        </p>
        <ng-template #noVehicle><p class="loc-scan-file loc-scan-file--empty">No file uploaded</p></ng-template>
        <label class="loc-check" *ngIf="scanJobForType('vehicle') as vJob">
          <input type="checkbox" [checked]="scanJobSelected(vJob.id)" (change)="toggleScanJob(vJob.id)" /> Include in processing
        </label>
        <div class="loc-scan-card-actions" *ngIf="canManageLocations()">
          <input
            #vehicleFileInput
            type="file"
            class="loc-scan-file-input"
            accept=".las,.laz,.LAS,.LAZ"
            (change)="onEmbeddedFileSelected($event, 'vehicle')"
          />
          <button
            type="button"
            class="btn secondary btn-compact"
            [disabled]="saving()"
            (click)="embedded ? triggerEmbeddedUpload('vehicle', vehicleFileInput) : openUploadScan('vehicle')"
          >
            {{ saving() && scanUploadForm.scanType === 'vehicle' ? 'Uploading…' : 'Upload vehicle' }}
          </button>
          <button
            type="button"
            class="btn-link loc-scan-remove"
            *ngIf="scanJobForType('vehicle') as vJob"
            [disabled]="!canRemoveScan(vJob) || deleteImpactLoadingId() === vJob.id || removingScanJobId() === vJob.id"
            (click)="removeScanJob(vJob)"
          >
            {{
              removingScanJobId() === vJob.id
                ? 'Removing…'
                : deleteImpactLoadingId() === vJob.id
                  ? 'Checking…'
                  : 'Remove'
            }}
          </button>
        </div>
      </div>
    </div>

    <div class="loc-scan-actions">
      <button
        type="button"
        class="btn secondary"
        *ngIf="canManageLocations()"
        (click)="loadDemoLidar()"
        [disabled]="demoLoading()"
      >
        {{ demoLoading() ? 'Loading…' : 'Load reference LiDAR' }}
      </button>
      <button
        type="button"
        class="btn primary"
        *ngIf="canManageLocations()"
        (click)="processSelectedScans()"
        [disabled]="processLoading() || !canProcessScans()"
      >
        {{ processLoading() ? 'Processing…' : 'Process selected' }}
      </button>
      <button type="button" class="btn secondary" *ngIf="canManageLocations()" (click)="runRuleCheckForLocation()" [disabled]="ruleCheckLoading()">
        {{ ruleCheckLoading() ? 'Checking…' : 'Run bye-law check' }}
      </button>
    </div>

    <details class="loc-history" *ngIf="loc.lidarScans?.length">
      <summary>Scan history</summary>
      <ul>
        <li *ngFor="let s of loc.lidarScans">{{ scanLabel(s) }} · {{ s.scanDate }} · {{ formatScanStatus(s.status) }}</li>
      </ul>
    </details>
  </ng-container>
</div>

<app-confirm-dialog
  [open]="deleteConfirmOpen()"
  title="Delete LiDAR file?"
  [message]="deleteConfirmMessage()"
  confirmLabel="Delete"
  cancelLabel="Cancel"
  [destructive]="true"
  (confirm)="confirmRemoveScanJob()"
  (cancel)="cancelRemoveScanJob()"
/>

<div class="laris-overlay" *ngIf="uploadScanOpen() && !embedded" (click)="closeUploadScan()">
  <div class="laris-modal" role="dialog" aria-modal="true" (click)="$event.stopPropagation()">
    <header class="laris-modal-head"><h3>Upload LiDAR scan</h3></header>
    <div class="laris-modal-body">
      <p class="laris-modal-form-error" *ngIf="uploadFormErrors().form" role="alert">
        {{ uploadFormErrors().form }}
      </p>
      <label class="laris-field">
        Survey type
        <select [(ngModel)]="scanUploadForm.scanType">
          <option value="drone">Drone</option>
          <option value="vehicle">Vehicle</option>
        </select>
      </label>
      <label class="laris-field" [class.laris-field--invalid]="uploadFormErrors().file">
        File
        <input type="file" accept=".las,.laz,.LAS,.LAZ" (change)="onScanFileSelected($event)" />
        <span class="ws-field-error" *ngIf="uploadFormErrors().file">{{ uploadFormErrors().file }}</span>
      </label>
      <p class="lidar-upload-loc-note" *ngIf="detail() as loc">
        Uploading to <strong>{{ loc.name }}</strong> ({{ loc.code }})
      </p>
    </div>
    <footer class="laris-modal-foot">
      <button type="button" class="btn secondary" (click)="closeUploadScan()">Cancel</button>
      <button type="button" class="btn primary" (click)="submitUploadScan()" [disabled]="saving()">Upload</button>
    </footer>
  </div>
</div>
